import { format, isBefore, isValid } from 'date-fns'
import { useMemo } from 'react'
import type { DateRange } from 'react-day-picker'
import { z } from 'zod'
import { DateRangePicker } from '@/components/commons/date-picker'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { CalendarIcon } from '@/components/ui/icons'
import { Table } from '@/components/ui/table'
import { formatDate, parseDate } from '@/dates'
import { useGetVisualVettingAccuracy } from '@/features/luxe-submissions/queries/use-get-visual-vetting-accuracy'
import type { VisualVettingAccuracyResponse } from '@/features/luxe-submissions/types'
import { cn } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { type TKey, useTranslation } from '@/translations/i18n'

const DATE_FORMAT = 'yyyy-MM-dd'

const DATE_PRESETS = [
	'today',
	'yesterday',
	'last_7_days',
	'this_week',
	'this_month',
	'last_month',
	'this_year'
] as const

const SKELETON_ROW_COUNT = 8

const DEFAULT_BUCKETS = [
	'totalListings',
	'passed',
	'failed',
	'escalated',
	'needsReviewAccuracy',
	'auditAccuracy',
	'overrideAccuracy',
	'overallAccuracy',
	'casesInNeedsReview',
	'audited',
	'disagreedWithScorecard'
] as const

const ACCURACY_BUCKETS = new Set([
	'needsReviewAccuracy',
	'auditAccuracy',
	'overrideAccuracy',
	'overallAccuracy'
])

const BUCKET_LABEL_KEYS: Record<string, TKey> = {
	totalListings: 'luxe_submissions:mp_conversion_bucket_total_listings',
	// Appended last to `buckets` by luxe-vv2-acc-active-assignments; absent from
	// the array entirely with the flag off, so no column renders.
	activeAssignments:
		'luxe_submissions:mp_conversion_bucket_active_assignments',
	yes: 'luxe_submissions:mp_conversion_bucket_yes',
	no: 'luxe_submissions:mp_conversion_bucket_no',
	passed: 'luxe_submissions:mp_conversion_bucket_passed',
	failed: 'luxe_submissions:mp_conversion_bucket_failed',
	escalated: 'luxe_submissions:mp_conversion_bucket_escalated',
	needsReviewAccuracy:
		'luxe_submissions:mp_conversion_bucket_needs_review_accuracy',
	auditAccuracy: 'luxe_submissions:mp_conversion_bucket_audit_accuracy',
	overrideAccuracy: 'luxe_submissions:mp_conversion_bucket_override_accuracy',
	overallAccuracy: 'luxe_submissions:mp_conversion_bucket_overall_accuracy',
	casesInNeedsReview:
		'luxe_submissions:mp_conversion_bucket_cases_in_needs_review',
	audited: 'luxe_submissions:mp_conversion_bucket_audited',
	disagreedWithScorecard:
		'luxe_submissions:mp_conversion_bucket_disagreed_with_scorecard'
}

const TOTAL_LISTINGS_KEY = 'totalListings'
const BUCKET_COLUMN_WIDTH = 192
const TOTAL_COLUMN_WIDTH = 140

const MpConversionSearchParamsSchema = z.object({
	from: z.string().optional(),
	to: z.string().optional()
})

type TFunction = ReturnType<typeof useTranslation>['t']

const formatBucketHeader = (t: TFunction, value: string) => {
	const key = BUCKET_LABEL_KEYS[value]
	return key ? t(key) : value
}

const formatMember = (row: VisualVettingAccuracyResponse['rows'][number]) => {
	const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ')
	return fullName || row.email
}

const formatBucketValue = (bucket: string, value: number | null) => {
	if (value === null) {
		return '–'
	}
	if (ACCURACY_BUCKETS.has(bucket)) {
		return `${(value * 100).toFixed(0)}%`
	}
	return value.toLocaleString()
}

interface AccuracyTableProps {
	data?: VisualVettingAccuracyResponse
	isLoading: boolean
	memberLabel: string
	totalLabel: string
}

function AccuracyTable({
	data,
	isLoading,
	memberLabel,
	totalLabel
}: AccuracyTableProps) {
	const { t } = useTranslation()
	const buckets = data?.buckets ?? DEFAULT_BUCKETS
	const rows = data?.rows ?? []
	const totalsByBucket = data?.totalsByBucket ?? []

	const totalIdx = buckets.indexOf(TOTAL_LISTINGS_KEY)
	const orderedIndices = buckets
		.map((_, idx) => idx)
		.filter((idx) => idx !== totalIdx)
	const orderedBuckets = orderedIndices.map((idx) => buckets[idx])
	const totalGrand =
		totalIdx === -1 ? null : (totalsByBucket[totalIdx] ?? null)

	return (
		<div className='flex h-full w-full flex-col overflow-auto rounded-md border border-neutral-200'>
			<Table.Root style={{ minWidth: '100%' }}>
				<Table.Header>
					<Table.HeaderRow>
						<Table.HeaderCell
							className='sticky top-0 z-3 rounded-bl-none! shadow-[inset_-1px_0_0_var(--color-neutral-200),inset_0_-1px_0_var(--color-neutral-200)]'
							pinningPosition='left'
							style={{ width: 260 }}
						>
							<Table.HeaderCellLabel>
								{memberLabel}
							</Table.HeaderCellLabel>
						</Table.HeaderCell>
						{orderedBuckets.map((bucket) => (
							<Table.HeaderCell
								className='sticky top-0 z-2 whitespace-nowrap border-neutral-200 border-r shadow-[inset_0_-1px_0_var(--color-neutral-200)]'
								key={bucket}
								style={{ width: BUCKET_COLUMN_WIDTH }}
							>
								<Table.HeaderCellLabel className='mx-auto'>
									{formatBucketHeader(t, bucket)}
								</Table.HeaderCellLabel>
							</Table.HeaderCell>
						))}
						{totalIdx !== -1 && (
							<Table.HeaderCell
								className='sticky top-0 z-3 rounded-br-none! shadow-[inset_1px_0_0_var(--color-neutral-200),inset_0_-1px_0_var(--color-neutral-200)]'
								pinningPosition='right'
								style={{ width: TOTAL_COLUMN_WIDTH }}
							>
								<Table.HeaderCellLabel className='mx-auto'>
									{formatBucketHeader(t, TOTAL_LISTINGS_KEY)}
								</Table.HeaderCellLabel>
							</Table.HeaderCell>
						)}
					</Table.HeaderRow>
				</Table.Header>
				<Table.Body>
					{isLoading
						? Array.from({ length: SKELETON_ROW_COUNT }).map(
								(_, rowIdx) => (
									<Table.Row
										// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder rows
										key={`skeleton-${rowIdx}`}
									>
										<Table.Cell
											className='h-12 shadow-[inset_-1px_0_0_var(--color-neutral-200)]'
											pinningPosition='left'
										>
											<Table.CellSkeleton />
										</Table.Cell>
										{orderedBuckets.map((bucket) => (
											<Table.Cell
												className='h-12 border-neutral-200 border-r'
												key={bucket}
											>
												<Table.CellSkeleton />
											</Table.Cell>
										))}
										{totalIdx !== -1 && (
											<Table.Cell
												className='h-12 shadow-[inset_1px_0_0_var(--color-neutral-200)]'
												pinningPosition='right'
											>
												<Table.CellSkeleton />
											</Table.Cell>
										)}
									</Table.Row>
								)
							)
						: rows.map((row) => (
								<Table.Row key={row.userId}>
									<Table.Cell
										className='h-12 font-medium text-neutral-900 shadow-[inset_-1px_0_0_var(--color-neutral-200)]'
										pinningPosition='left'
									>
										{formatMember(row)}
									</Table.Cell>
									{orderedIndices.map((idx) => {
										const bucket = buckets[idx]
										const value = row.values[idx] ?? null
										return (
											<Table.Cell
												className={cn(
													'h-12 border-neutral-200 border-r text-center tabular-nums',
													value === null
														? 'text-neutral-300'
														: 'text-neutral-700'
												)}
												key={`${row.userId}-${bucket}`}
											>
												{formatBucketValue(
													bucket,
													value
												)}
											</Table.Cell>
										)
									})}
									{totalIdx !== -1 && (
										<Table.Cell
											className='h-12 text-center font-medium text-neutral-900 tabular-nums shadow-[inset_1px_0_0_var(--color-neutral-200)]'
											pinningPosition='right'
										>
											{formatBucketValue(
												TOTAL_LISTINGS_KEY,
												row.values[totalIdx] ?? null
											)}
										</Table.Cell>
									)}
								</Table.Row>
							))}
					{!isLoading && (
						<Table.Row>
							<Table.Cell
								className='sticky bottom-0 z-3 h-12 font-medium text-neutral-900 uppercase shadow-[inset_-1px_0_0_var(--color-neutral-200),inset_0_1px_0_var(--color-neutral-200)]'
								pinningPosition='left'
							>
								{totalLabel}
							</Table.Cell>
							{orderedIndices.map((idx) => {
								const bucket = buckets[idx]
								return (
									<Table.Cell
										className='sticky bottom-0 z-2 h-12 border-neutral-200 border-r text-center font-medium text-neutral-900 tabular-nums shadow-[inset_0_1px_0_var(--color-neutral-200)]'
										key={`total-${bucket}`}
									>
										{formatBucketValue(
											bucket,
											totalsByBucket[idx] ?? null
										)}
									</Table.Cell>
								)
							})}
							{totalIdx !== -1 && (
								<Table.Cell
									className='sticky right-0 bottom-0 z-3 h-12 text-center font-semibold text-neutral-900 tabular-nums shadow-[inset_1px_0_0_var(--color-neutral-200),inset_0_1px_0_var(--color-neutral-200)]'
									pinningPosition='right'
								>
									{formatBucketValue(
										TOTAL_LISTINGS_KEY,
										totalGrand
									)}
								</Table.Cell>
							)}
						</Table.Row>
					)}
				</Table.Body>
			</Table.Root>
		</div>
	)
}

function LuxeSubmissionsMpConversion() {
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		MpConversionSearchParamsSchema
	)

	const today = format(new Date(), DATE_FORMAT)

	// Default range is today → today. Fall back to today for missing/invalid params.
	const from =
		searchParams.from && isValid(parseDate(searchParams.from))
			? searchParams.from
			: today
	const parsedTo =
		searchParams.to && isValid(parseDate(searchParams.to))
			? searchParams.to
			: today
	// Guard against an inverted range coming from the URL.
	const to = isBefore(parseDate(parsedTo), parseDate(from)) ? from : parsedTo

	const selectedRange = useMemo<DateRange>(
		() => ({ from: parseDate(from), to: parseDate(to) }),
		[from, to]
	)

	const dateLabel =
		from === to
			? formatDate(from, 'MMM d, yyyy')
			: `${formatDate(from, 'MMM d, yyyy')} - ${formatDate(to, 'MMM d, yyyy')}`

	const handleRangeChange = (range?: DateRange) => {
		if (range?.from && range?.to) {
			const nextFrom = format(range.from, DATE_FORMAT)
			const nextTo = format(range.to, DATE_FORMAT)

			setSearchParams({ from: nextFrom, to: nextTo })
		}
	}

	const { data, isLoading, isError } = useGetVisualVettingAccuracy({
		granularity: 'daily',
		from,
		to
	})

	const teamTotals = useMemo(() => {
		if (!data) {
			return []
		}
		const totalIdx = data.buckets.indexOf(TOTAL_LISTINGS_KEY)
		if (totalIdx === -1) {
			return []
		}
		const byRole = new Map<string, number>()
		for (const row of data.rows) {
			const value = row.values[totalIdx] ?? 0
			byRole.set(row.role, (byRole.get(row.role) ?? 0) + value)
		}
		return Array.from(byRole.entries())
			.map(([role, total]) => ({ role, total }))
			.sort((a, b) => b.total - a.total)
	}, [data])

	const formatRoleLabel = (role: string) =>
		role
			.split('_')
			.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
			.join(' ')

	return (
		<div className='flex flex-1 flex-col overflow-hidden'>
			<div className='flex items-center justify-between border-neutral-200 border-b px-10 pt-6 pb-6'>
				<h1 className='font-medium text-lg text-neutral-900'>
					{t('luxe_submissions:mp_conversion_title')}
				</h1>
			</div>

			<div className='flex flex-1 flex-col gap-4 overflow-hidden px-10 py-6'>
				<div className='flex shrink-0 items-center justify-between gap-4'>
					{teamTotals.length > 0 ? (
						<div className='flex items-center gap-3 overflow-x-auto'>
							<span className='shrink-0 font-medium text-neutral-500 text-xs uppercase'>
								{t(
									'luxe_submissions:mp_conversion_team_volume'
								)}
							</span>
							{teamTotals.map((teamRow) => (
								<div
									className='flex shrink-0 items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-white px-3 py-1.5'
									key={teamRow.role}
								>
									<span className='whitespace-nowrap text-neutral-500 text-sm'>
										{formatRoleLabel(teamRow.role)}
									</span>
									<span className='font-semibold text-base text-neutral-900 tabular-nums'>
										{teamRow.total.toLocaleString()}
									</span>
								</div>
							))}
						</div>
					) : (
						<div />
					)}

					<div className='shrink-0'>
						<DateRangePicker
							desktopNumberOfMonths={2}
							disabled={{ after: new Date() }}
							hasConfirm
							presets={[...DATE_PRESETS]}
							setValue={handleRangeChange}
							showOutsideDays={false}
							trigger={
								<ButtonLegacy
									size='sm'
									type='button'
									variant='outline'
								>
									<CalendarIcon className='text-neutral-700' />
									{dateLabel}
								</ButtonLegacy>
							}
							value={selectedRange}
						/>
					</div>
				</div>

				{isError ? (
					<div className='flex flex-1 items-center justify-center text-neutral-500 text-sm'>
						{t('common:error_table_title')}
					</div>
				) : (
					<AccuracyTable
						data={data}
						isLoading={isLoading}
						memberLabel={t(
							'luxe_submissions:team_productivity_member'
						)}
						totalLabel={t(
							'luxe_submissions:team_productivity_total'
						)}
					/>
				)}
			</div>
		</div>
	)
}

export { LuxeSubmissionsMpConversion }
